import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useRealtime } from '@/hooks/useRealtime';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

interface ProblemStatement {
  id: string;
  title: string;
  description: string | null;
  domain_id: string | null;
  difficulty: string | null;
  attachments: unknown;
  is_published: boolean;
}

interface Domain {
  id: string;
  name: string;
  description: string;
}

const difficultyColor = (d: string) => {
  switch (d) {
    case 'Advanced': return 'bg-red-100 text-red-700';
    case 'Intermediate': return 'bg-amber-100 text-amber-700';
    case 'Beginner': return 'bg-accent-100 text-accent-700';
    default: return 'bg-background-200 text-foreground-500';
  }
};

export default function DomainProblemsPage() {
  const { domainId } = useParams<{ domainId: string }>();
  const [domain, setDomain] = useState<Domain | null>(null);
  const [statements, setStatements] = useState<ProblemStatement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    if (!domainId) return;
    try {
      setLoading(true);
      const [domRes, psRes, settingsRes] = await Promise.all([
        supabase.from('domains').select('*').eq('id', domainId).single(),
        supabase.from('problem_statements').select('*').eq('domain_id', domainId).order('created_at', { ascending: false }),
        supabase.from('settings').select('value').eq('key', 'problem_statements_published').single(),
      ]);

      if (domRes.error) throw domRes.error;
      if (psRes.error) throw psRes.error;

      const publishedSetting = settingsRes.data?.value === 'true';
      setDomain(domRes.data);

      let filtered = (psRes.data || []);
      if (publishedSetting) {
        filtered = filtered.filter((ps) => ps.is_published === true);
      }
      setStatements(filtered);
      setError('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load problem statements');
    } finally {
      setLoading(false);
    }
  }, [domainId]);

  useEffect(() => { loadData(); }, [loadData]);

  useRealtime<ProblemStatement>({
    table: 'problem_statements',
    onAny: () => loadData(),
  });

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-16">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-10 h-10 rounded-full border-2 border-accent-500 border-t-transparent animate-spin"></div>
            </div>
          ) : error ? (
            <div className="py-20 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <i className="ri-error-warning-line text-2xl text-red-500"></i>
              </div>
              <p className="text-foreground-500 text-sm">{error}</p>
            </div>
          ) : !domain ? (
            <div className="py-20 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-stack-line text-2xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-400 text-sm">Domain not found</p>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <a href="/#tracks" className="inline-flex items-center gap-1 text-sm text-foreground-500 hover:text-foreground-700 transition-colors mb-4 cursor-pointer">
                  <i className="ri-arrow-left-line"></i> Back to Tracks
                </a>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-2">{domain.name}</h1>
                <p className="text-foreground-500 text-sm">{domain.description}</p>
              </div>

              {statements.length === 0 ? (
                <div className="py-16 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-question-answer-line text-2xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-400 text-sm">No problem statements available</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {statements.map((ps) => (
                    <div key={ps.id} className="bg-background-50 border border-background-200/70 rounded-2xl p-6">
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <h3 className="font-heading text-lg font-semibold text-foreground-900">{ps.title}</h3>
                        {ps.difficulty && (
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold ${difficultyColor(ps.difficulty)}`}>
                            {ps.difficulty}
                          </span>
                        )}
                      </div>
                      {ps.description && (
                        <p className="text-sm text-foreground-600 leading-relaxed">{ps.description}</p>
                      )}
                      {ps.attachments && (
                        <div className="mt-3 flex items-center gap-2">
                          <i className="ri-attachment-line text-foreground-400 text-sm"></i>
                          <span className="text-xs text-foreground-500">Attachments available</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}